import React, { useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

// The message an unsaved-changes guard hands the router. The router's getUserConfirmation
// recognises it and opens the shared modal below instead of the browser's confirm box.
export const UNSAVED_CHANGES_MESSAGE = 'unsaved-changes';

// Asks whether to leave a page with unsaved changes. "Stay on page" is the focused default,
// and Esc or a click outside chooses it too. There is deliberately no "Save and leave":
// a save can fail validation or fail on the server.
export const UnsavedChangesModal = ({ onChoose }) => {
  const [isOpen, setIsOpen] = useState(true);
  const leave = useRef(false);
  const stayButton = useRef(null);

  const choose = (discard) => {
    if (!isOpen) return;
    leave.current = discard;
    setIsOpen(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={() => choose(false)}
      autoFocus={false}
      returnFocusAfterClose={false}
      onOpened={() => stayButton.current?.focus()}
      onClosed={() => onChoose(leave.current)}
      centered
    >
      <ModalHeader toggle={() => choose(false)}>Discard unsaved changes?</ModalHeader>
      <ModalBody>
        You have changes on this page that haven't been saved. If you leave now, they will be lost.
      </ModalBody>
      <ModalFooter>
        <Button color="light" innerRef={stayButton} onClick={() => choose(false)}>
          Stay on page
        </Button>
        <Button color="danger" onClick={() => choose(true)}>
          Discard changes
        </Button>
      </ModalFooter>
    </Modal>
  );
};

// BrowserRouter's getUserConfirmation: called whenever a <Prompt> blocks a navigation.
// Guard prompts get the shared modal; any other prompt message keeps the browser's confirm.
// `callback(true)` lets the navigation through, `callback(false)` cancels it.
export const confirmNavigation = (message, callback) => {
  if (message !== UNSAVED_CHANGES_MESSAGE) {
    callback(window.confirm(message));
    return;
  }

  const container = document.createElement('div');
  document.body.appendChild(container);
  const onChoose = (discard) => {
    ReactDOM.unmountComponentAtNode(container);
    container.remove();
    callback(discard);
  };
  ReactDOM.render(<UnsavedChangesModal onChoose={onChoose} />, container);
};
